"use client";

/**
 * ArtworkFxControl — a front-end control that lets ANY listener turn on the
 * sound-reactive Artwork FX on a cover, pick the effect, and tune strength +
 * colour. It's a PERSONAL, per-device preference (localStorage), broadcast so
 * every mounted instance (release page, track page, media-player window) stays
 * in sync. It never writes to the release/work doc — the creator's own FX
 * (set in the admin Design panel) remains the default ("Auto").
 */

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ARTWORK_FX } from "@/components/visual/audio-reactive-artwork";
import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";

const LEGACY_KEY = "bb-artwork-fx";       // old single global pref
const MAP_KEY = "bb-artwork-fx-map";      // per-track prefs: { [trackId|"__default"]: FxSettings }
const EVENT = "bb:artwork-fx";
const DEFAULT_BUCKET = "__default";

export interface FxSettings {
  /** Multi-select — several effects stacked. `effect` kept as the first for
   *  backward compat with older single-effect consumers/storage. */
  effects: string[];
  intensity: number;
  color: string;
  effect?: string;
}
/** null = "Auto" — use each item's own creator-set FX. */
export type FxPref = FxSettings | null;

function readMap(): Record<string, any> {
  try { return JSON.parse(localStorage.getItem(MAP_KEY) || "{}") || {}; } catch { return {}; }
}
function writeMap(m: Record<string, any>) {
  try { localStorage.setItem(MAP_KEY, JSON.stringify(m)); } catch { /* ignore */ }
}

/** Read the saved FX pref for a specific track (or the default bucket). Each
 *  track remembers its OWN choice — changing FX on one track never touches
 *  another (Gordon: "remember the fx setting for each track, for each user"). */
function readFor(trackId?: string): FxPref {
  try {
    const m = readMap();
    const key = trackId || DEFAULT_BUCKET;
    let p: any = m[key];
    // One-time migration of the old global pref → the default bucket.
    if (!p && key === DEFAULT_BUCKET) {
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy) { try { p = JSON.parse(legacy); } catch { /* ignore */ } }
    }
    if (!p) return null;
    const effects: string[] = Array.isArray(p.effects)
      ? p.effects.filter((e: any) => e && e !== "off")
      : (p.effect && p.effect !== "off" ? [p.effect] : []);
    return { effects, intensity: p.intensity ?? 1, color: p.color ?? "", effect: effects[0] };
  } catch { return null; }
}

/** Personal, per-track Artwork FX preference. Pass the current track's id so the
 *  choice is remembered per track; omit it for track-less contexts. */
export function useArtworkFx(trackId?: string): [FxPref, (p: FxPref) => void] {
  const [pref, setPrefState] = useState<FxPref>(null);
  useEffect(() => {
    const sync = () => setPrefState(readFor(trackId));
    sync();
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener(EVENT, sync); window.removeEventListener("storage", sync); };
  }, [trackId]);
  const setPref = (p: FxPref) => {
    try {
      const m = readMap();
      const key = trackId || DEFAULT_BUCKET;
      if (p) m[key] = { effects: p.effects, intensity: p.intensity, color: p.color, effect: p.effects[0] };
      else delete m[key];
      writeMap(m);
    } catch { /* ignore */ }
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(EVENT));
    setPrefState(p);
  };
  return [pref, setPref];
}

/** Effective FX: the listener's personal choice wins; otherwise the item's own.
 *  Returns `effects[]` (multi-select) plus `effect` = first, for compatibility. */
export function resolveFx(pref: FxPref, itemDefault?: Partial<FxSettings>): FxSettings {
  if (pref) {
    const effects = pref.effects?.length ? pref.effects : (pref.effect && pref.effect !== "off" ? [pref.effect] : []);
    return { effects, effect: effects[0] || "off", intensity: pref.intensity, color: pref.color };
  }
  const effects = itemDefault?.effects?.length
    ? itemDefault.effects
    : (itemDefault?.effect && itemDefault.effect !== "off" ? [itemDefault.effect] : []);
  return {
    effects,
    effect: effects[0] || "off",
    intensity: itemDefault?.intensity ?? 1,
    color: itemDefault?.color || "",
  };
}

export function ArtworkFxControl({
  itemDefault,
  className,
  openDir = "up",
  trackId,
}: {
  itemDefault?: Partial<FxSettings>;
  className?: string;
  openDir?: "up" | "down";
  /** Remember this control's FX choice against a specific track. */
  trackId?: string;
}) {
  const [pref, setPref] = useArtworkFx(trackId);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const eff = resolveFx(pref, itemDefault);
  const isAuto = !pref;

  // Phones get a bottom SHEET instead of the anchored popover — the popover
  // was clipped by the artwork frame's overflow-hidden on mobile (Gordon
  // 2026-09-11: "the fx pop up selector is hidden on mobile").
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const upd = () => setIsMobile(mq.matches);
    upd();
    mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);

  useEffect(() => {
    if (!open) return;
    // pointerdown covers touch + mouse; the sheet lives in a portal, so check
    // both the trigger wrapper AND the panel itself.
    const onDoc = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, [open]);

  // Multi-select: toggle an effect in/out. "__auto" resets to the item default;
  // "off" (None) clears all selected effects.
  const toggle = (effect: string) => {
    if (effect === "__auto") { setPref(null); return; }
    const base = {
      intensity: pref?.intensity ?? (itemDefault?.intensity ?? 1),
      color: pref?.color ?? (itemDefault?.color ?? ""),
    };
    if (effect === "off") { setPref({ effects: [], ...base }); return; }
    const cur = eff.effects || [];
    const next = cur.includes(effect) ? cur.filter(e => e !== effect) : [...cur, effect];
    setPref({ effects: next, ...base });
  };

  const count = eff.effects?.length || 0;
  const activeLabel = isAuto
    ? "Auto"
    : count === 0
      ? "Off"
      : count === 1
        ? (ARTWORK_FX.find(o => o.id === eff.effects[0])?.label || "On")
        : `${count} FX`;

  return (
    <div ref={ref} className={cn("pointer-events-auto", className)}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-lg backdrop-blur hover:bg-black/80"
        title="Artwork FX — reacts to the sound"
        aria-label="Artwork FX"
      >
        <Sparkles className="h-3.5 w-3.5" /> FX{!isAuto && count > 0 ? `: ${activeLabel}` : ""}
      </button>

      {open && (() => {
        // Shared panel body — every tap applies to the artwork INSTANTLY
        // (setPref broadcasts to all mounted artworks); there is no save step.
        const body = (
          <>
            <p className="text-[11px] font-medium text-white/60">Artwork FX · reacts to the sound · pick more than one</p>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => toggle("__auto")}
                className={cn("rounded-full px-3 py-1.5 text-[11px]", isAuto ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25")}
                title="Use the creator's own setting"
              >
                Auto
              </button>
              {ARTWORK_FX.map(o => {
                const active = o.id === "off"
                  ? (!isAuto && count === 0)
                  : (!isAuto && (eff.effects || []).includes(o.id));
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => toggle(o.id)}
                    title={o.hint}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-[11px]",
                      active ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25"
                    )}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            {!isAuto && count > 0 && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[11px] text-white/60">Strength</span>
                <input
                  type="range" min={0} max={1.5} step={0.05} value={eff.intensity}
                  onChange={(e) => setPref({ effects: eff.effects, intensity: parseFloat(e.target.value), color: eff.color })}
                  className="flex-1 accent-white"
                />
                <input
                  type="color" value={eff.color || "#ff3366"}
                  onChange={(e) => setPref({ effects: eff.effects, intensity: eff.intensity, color: e.target.value })}
                  className="h-6 w-7 cursor-pointer rounded border border-white/20 bg-transparent"
                  title="Accent colour"
                />
              </div>
            )}
            <p className="text-[10px] text-white/40">Applies instantly, no save needed — remembered on this device. Looks best while the track is playing.</p>
          </>
        );

        // MOBILE: bottom sheet in a portal — the anchored popover was clipped
        // by the artwork frame's overflow-hidden and unreachable on phones.
        if (isMobile && typeof document !== "undefined") {
          return createPortal(
            <div className="fixed inset-0 z-[110] sm:hidden" onClick={() => setOpen(false)}>
              <div className="absolute inset-0 bg-black/50" />
              <div
                ref={panelRef}
                onClick={(e) => e.stopPropagation()}
                className="absolute inset-x-0 bottom-0 max-h-[70dvh] overflow-y-auto rounded-t-2xl bg-black/95 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] text-white shadow-2xl backdrop-blur-md space-y-2.5"
              >
                <div className="mx-auto h-1 w-10 rounded-full bg-white/25" aria-hidden />
                {body}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-full rounded-lg bg-white text-black text-sm font-semibold py-2.5 mt-1"
                >
                  Done
                </button>
              </div>
            </div>,
            document.body,
          );
        }

        // DESKTOP: anchored popover, scroll-capped so it can never clip.
        return (
          <div
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "absolute right-0 z-[40] w-64 max-h-[min(60vh,340px)] overflow-y-auto rounded-xl bg-black/90 p-3 text-white shadow-2xl backdrop-blur-md space-y-2",
              openDir === "up" ? "bottom-full mb-2" : "top-full mt-2"
            )}
          >
            {body}
          </div>
        );
      })()}
    </div>
  );
}
